import { DUPE_API_URL } from '@dupe/common/constants'
import { useQuery } from '@tanstack/react-query'
import useAuthUser from '~/src/hooks/useAuthUser'
import fetcher from '~/src/lib/fetcher'

export interface BrandPartnerStats {
  shopKey: string
  displayName: string | null
  networks: string[]
  commissionRate: { min: number; max: number } | null
  realizedTakeRate: number | null
  gmv30d: number
  revenue30d: number
  transactions30d: number
  clicks30d: number
  refreshedAt: string | null
}

/**
 * Admin-only: fetch the trailing-30d partner stats for a shop — networks,
 * commission range, realized take rate, revenue/GMV, transactions, and
 * clicks. Backed by the `shop_partner_stats_30d` matview (refreshed nightly),
 * so values are at-most ~24h stale; the hook's `staleTime` matches that.
 * No-op for non-admins.
 */
export function useBrandPartnerStats(
  shopKey: string | undefined,
  options?: { enabled?: boolean }
) {
  const { authUser, isAdmin } = useAuthUser()
  const enabled = Boolean(
    authUser && isAdmin && shopKey && (options?.enabled ?? true)
  )

  return useQuery<BrandPartnerStats>({
    queryKey: ['admin-brand-partner-stats', shopKey],
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      return fetcher<BrandPartnerStats>(
        `${DUPE_API_URL}/api/admin/shops/${encodeURIComponent(shopKey!)}/partner-stats`,
        { method: 'GET', authUser }
      )
    },
  })
}
